/**
 * AdminBoardController — 게시판 edit 화면의 인라인 필드/옵션 패널 동작.
 *
 * 모든 URL은 HTML data-* 속성으로 전달받음 (절대규칙 #4 — TS에 라우트 하드코딩 금지).
 * - 필드 추가/삭제, 드래그 정렬 (POST/DELETE)
 * - 속성 편집: ⋮ 버튼 → FieldPropertyPanel 오버레이 (PATCH)
 *
 * 마크업 컨벤션은 resources/views/admin/pages/boards/_fields-panel.php 참조.
 */
import Sortable from "sortablejs";
import { Toast } from "../../utils/Toast";
import { Modal } from "../../utils/Modal";
import { CardSelect } from "../../components/CardSelect";
import { openFieldPropertyPanel } from "../../components/FieldPropertyPanel";

type JsonResult = {
    result?: string;
    msg?: string;
    data?: Record<string, unknown>;
};

function csrfToken(): string {
    return (
        document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ??
        document.querySelector<HTMLInputElement>('input[name="_token"]')?.value ??
        ""
    );
}

async function postJson(url: string, body: unknown, method = "POST"): Promise<JsonResult> {
    const res = await fetch(url, {
        method,
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "X-CSRF-TOKEN": csrfToken(),
        },
        body: JSON.stringify(body ?? {}),
    });
    const text = await res.text();
    let json: JsonResult = {};
    try {
        json = JSON.parse(text) as JsonResult;
    } catch {
        json = { result: "fail", msg: text || `HTTP ${res.status}` };
    }
    return json;
}

async function submitForm(form: HTMLFormElement, url: string, method?: string): Promise<JsonResult> {
    const fd = new FormData(form);
    if (!method) {
        method = (fd.get("_method") as string | null) ?? form.method.toUpperCase() ?? "POST";
        fd.delete("_method");
    } else {
        fd.delete("_method");
    }
    const res = await fetch(url, {
        method,
        headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "X-CSRF-TOKEN": csrfToken(),
        },
        body: fd,
    });
    const text = await res.text();
    let json: JsonResult = {};
    try {
        json = JSON.parse(text) as JsonResult;
    } catch {
        json = { result: "fail", msg: text || `HTTP ${res.status}` };
    }
    return json;
}

function bindFieldPanel(panel: HTMLElement): void {
    bindFieldNew(panel);
    bindFieldList(panel);
    initCardSelects(panel);
}

function initCardSelects(panel: HTMLElement): void {
    panel.querySelectorAll<HTMLSelectElement>("[data-card-select]").forEach((el) => {
        new CardSelect(el, {
            columns: Number(el.dataset.csgCols ?? "4"),
            onChange: (value) => syncMultipleFlag(el, value),
        }).init();
        syncMultipleFlag(el, el.value);
    });
}

function syncMultipleFlag(selectEl: HTMLSelectElement, value: string): void {
    const form = selectEl.closest("form");
    if (!form) return;
    const flag = form.querySelector<HTMLInputElement>("[data-multiple-flag]");
    if (!flag) return;
    flag.value = value === "select_multi" ? "1" : "0";
}

function bindFieldNew(panel: HTMLElement): void {
    const openBtn = panel.querySelector<HTMLButtonElement>("[data-field-new-open]");
    const modal = panel.querySelector<HTMLElement>("[data-field-new-modal]");
    if (!modal) return;
    const form = modal.querySelector<HTMLFormElement>("[data-field-new-form]");
    const submitBtn = modal.querySelector<HTMLButtonElement>("[data-field-new-submit]");
    const url = panel.dataset.fieldStoreUrl ?? "";
    if (!form || url === "") return;

    const openModal = (): void => {
        modal.hidden = false;
        modal.setAttribute("aria-hidden", "false");
        const first = form.querySelector<HTMLInputElement>('input[name="name"]');
        first?.focus();
    };
    const closeModal = (): void => {
        modal.hidden = true;
        modal.setAttribute("aria-hidden", "true");
        form.reset();
        const typeSel = form.querySelector<HTMLSelectElement>('select[name="type"]');
        if (typeSel) syncMultipleFlag(typeSel, typeSel.value);
    };

    openBtn?.addEventListener("click", openModal);
    modal.querySelectorAll<HTMLElement>("[data-field-new-close]").forEach((el) => {
        el.addEventListener("click", closeModal);
    });
    document.addEventListener("keydown", (ev) => {
        if (ev.key === "Escape" && !modal.hidden) closeModal();
    });

    submitBtn?.addEventListener("click", () => {
        if (!form.reportValidity()) return;
        form.requestSubmit();
    });

    form.addEventListener("submit", async (e) => {
        e.preventDefault();
        const typeSel = form.querySelector<HTMLSelectElement>('select[name="type"]');
        if (typeSel && typeSel.value === "select_multi") {
            typeSel.value = "select";
            const flag = form.querySelector<HTMLInputElement>("[data-multiple-flag]");
            if (flag) flag.value = "1";
        }
        const json = await submitForm(form, url, "POST");
        if (json.result === "success") {
            Toast.success(json.msg ?? "필드를 추가했습니다.");
            window.location.reload();
        } else {
            Toast.error(json.msg ?? "필드 추가 실패");
        }
    });
}

function bindFieldList(panel: HTMLElement): void {
    const list = panel.querySelector<HTMLElement>("[data-field-list]");
    if (!list) return;

    const reorderUrl = panel.dataset.fieldReorderUrl ?? "";
    if (reorderUrl !== "") {
        Sortable.create(list, {
            animation: 150,
            handle: ".admin-boards-fields__handle",
            ghostClass: "admin-sortable__ghost",
            dragClass: "admin-sortable__drag",
            draggable: "[data-field-card]",
            filter: "[data-options-row]",
            onEnd: async () => {
                const cards = Array.from(list.querySelectorAll<HTMLElement>("[data-field-card]"));
                const items = cards.map((el, idx) => ({ id: Number(el.dataset.fieldId ?? "0"), sort_order: (idx + 1) * 10 }));
                const json = await postJson(reorderUrl, { items }, "PATCH");
                if (json.result === "success") {
                    Toast.success(json.msg ?? "필드 순서를 저장했습니다.");
                    cards.forEach((el, idx) => {
                        const numEl = el.querySelector<HTMLElement>("[data-sort-num]");
                        if (numEl) numEl.textContent = String((idx + 1) * 10);
                    });
                } else {
                    Toast.error(json.msg ?? "필드 순서 저장 실패");
                }
            },
        });
    }

    list.addEventListener("click", (ev) => {
        const target = ev.target as HTMLElement;
        const card = target.closest<HTMLElement>("[data-field-card]");
        if (!card) return;

        if (target.closest("[data-field-props-btn]")) {
            const btn = target.closest<HTMLElement>("[data-field-props-btn]") as HTMLElement;
            openFieldPropertyPanel(btn, card);
            return;
        }
        if (target.closest("[data-field-destroy]")) {
            void destroyField(card);
            return;
        }
    });
}

async function destroyField(card: HTMLElement): Promise<void> {
    if (!(await Modal.confirm("이 필드를 삭제하면 해당 필드에 저장된 모든 게시글의 값이 함께 삭제됩니다. 진행할까요?"))) return;
    const url = card.dataset.destroyUrl ?? "";
    if (url === "") return;
    const json = await postJson(url, {}, "DELETE");
    if (json.result === "success") {
        Toast.success(json.msg ?? "필드를 삭제했습니다.");
        card.remove();
    } else {
        Toast.error(json.msg ?? "필드 삭제 실패");
    }
}

function bootFieldsPanel(): void {
    const panel = document.querySelector<HTMLElement>("[data-board-fields-panel]");
    if (panel) bindFieldPanel(panel);
}

export const AdminBoardController = {
    edit(): void { bootFieldsPanel(); },
    fields(): void { bootFieldsPanel(); },
};
